import React, { useState, useEffect } from "react";
import { Box, Paper, makeStyles } from "@material-ui/core";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Text } from "common";
import { useDispatch, useSelector } from "react-redux";
import {
  setBundlePromotionObject,
  sendPosInfoResoures,
} from "store/actions/bundlePromotion.actions";
import { IState } from "store/store.interface";
import { useApiInfo } from "hooks";
import { CREATE_RESOURCE } from "store/store.types";

const useStyles = makeStyles((theme) => ({
  section: {
    width: "100%",
  },
  paper: (props: { isDragging: boolean; fileError: string }) => ({
    border: `2px dashed ${props.fileError ? theme.palette.error.main : "#ccc"}`,
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: "center",
    backgroundColor: props.isDragging ? "#e3f2fd" : "white",
    transition: "background-color 0.3s",
  }),
  iconWrapper: {
    marginBottom: theme.spacing(2),
  },
  browseLink: {
    display: "block",
    marginTop: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface IProps {
  onCompleteUpload?: (data: any) => void;
  fileSelected: boolean;
  name: string;
  onValidateInput?: (error: any) => void;
  onFileSelect?: (file: File) => void;
  uploadStrategy?: "PARSE" | "UPLOAD" | "RETURN_FILE";
  disable?:boolean;
}

type Cell = string | number | null | undefined;
type Row = Cell[];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const CustomFileUpload: React.FC<IProps> = ({
  onCompleteUpload,
  fileSelected,
  name,
  onValidateInput,
  onFileSelect,
  uploadStrategy,
  disable,
}) => {
  const dispatch = useDispatch();

  const { posInfoResourceData } = useSelector(
    (state: IState) => state.bundlePromotion,
  );
  const { error } = useApiInfo(CREATE_RESOURCE);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string>("");
  const [selected, setSelected] = useState<boolean>(fileSelected || false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");
  const [hasUploaded, setHasUploaded] = useState(false);
  const resolvedStrategy = uploadStrategy ?? "PARSE";

  const classes = useStyles({ isDragging, fileError });

  const onComplete = (data: any) => {
    setSelected(true);
    setIsDragging(false);
    setIsUploading(false);
    onCompleteUpload?.(data);
  };

  function normalizeCell(cell: Cell): string {
    return cell != null ? cell.toString().trim() : "";
  }

  function normalizeRow(row: Row): string[] {
    return row.map(normalizeCell);
  }

  const importXLSX = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        blankrows: false,
      });

      const normalizedData = (rawData as Row[]).map(normalizeRow);
      onComplete(normalizedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const importCSV = (file: File) => {
    setIsUploading(true);
    Papa.parse(file, {
      worker: true,
      complete: (result) => onComplete(result.data),
      skipEmptyLines: true,
    });
  };

  const handleFileInput = async (event: any) => {
    if (event.target.files[0]) {
      handleFile(event.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    const fileType = file.name.split(".")?.[1].toLowerCase();
    const fileName = file.name.split(".")?.[0];
    setFileName(
      fileName?.charAt(0)?.toUpperCase() + fileName?.slice(1)?.toLowerCase(),
    );

    let isValid = false;
    let errorText = "";

    if (resolvedStrategy === "UPLOAD" || resolvedStrategy === "RETURN_FILE") {
      isValid = fileType === "csv";
      errorText = "Only CSV files under 10MB are supported.";
    } else {
      isValid = fileType === "csv" || fileType === "xlsx";
      errorText = "Only CSV or XLSX files under 10MB are supported.";
    }

    if (file.size > MAX_FILE_SIZE || !isValid) {
      setFileError(errorText);
      onValidateInput?.({ [name]: { isError: true } });
      setIsDragging(false);
      dispatch(setBundlePromotionObject(null, name));
      return;
    }

    setFileError("");
    setIsDragging(false);
    onValidateInput?.({ [name]: { isError: false } });

    if (resolvedStrategy === "RETURN_FILE" && onFileSelect) {
      onFileSelect(file);
    }

    if (resolvedStrategy === "UPLOAD" && fileType === "csv") {
      dispatch(setBundlePromotionObject(null, name));
      setFileError("");
      setIsUploading(true);
      setHasUploaded(true);

      const filePayload = new FormData();
      filePayload.append("file", file, file?.name);
      dispatch(sendPosInfoResoures(filePayload));
      setSelected(true);
    } else if (fileType === "csv") {
      importCSV(file);
    } else {
      importXLSX(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /* This is use to clear the error and reset the state */
  useEffect(() => {
    if (disable) {
      setFileError("");
      setHasUploaded(false);
      setIsUploading(false);
      setSelected(false);
    }
  }, [disable]);

  // This is used to set error if upload strategy is UPLOAD and there is an error in api response
  useEffect(() => {
    if (resolvedStrategy === "UPLOAD" && hasUploaded && error) {
      setFileError(error);
      setIsUploading(false);
      setHasUploaded(false);
    }
  }, [error, hasUploaded]);

  // This is used to set the posInfoResource id in the store after successful upload when upload strategy is UPLOAD
  useEffect(() => {
    if (
      resolvedStrategy === "UPLOAD" &&
      hasUploaded &&
      posInfoResourceData?.id
    ) {
      dispatch(setBundlePromotionObject(posInfoResourceData.id, name));
      setIsUploading(false);
      setHasUploaded(false);
      setFileError("");
    }
  }, [dispatch, posInfoResourceData, hasUploaded, resolvedStrategy, name]);

  return (
    <Box className={classes.section} data-testid={"file-upload-cart-bundle"}>
      <Paper
        elevation={0}
        className={classes.paper}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box className={classes.iconWrapper}>
          <svg
            style={{ margin: "0 auto", display: "block" }}
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#aaa"
            strokeWidth="2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </Box>
        <Box display="flex" justifyContent="center">
          <label htmlFor={`file-upload-${name}`}>
            <Text
              className={classes.browseLink}
              style={{ marginTop: 0, marginRight: "8px" }}
            >
              Upload a file
            </Text>
          </label>
          <Text color="textPrimary">or drag and drop</Text>
        </Box>
        <Text color="textSecondary">
          {resolvedStrategy === "PARSE"
            ? "CSV, XLSX up to 10MB"
            : "CSV up to 10MB"}
        </Text>
        <input
          type="file"
          accept={resolvedStrategy === "PARSE" ? ".csv,.xlsx" : ".csv"}
          id={`file-upload-${name}`}
          style={{ display: "none" }}
          onChange={handleFileInput}
          data-testid={`file-upload-${name}`}
          disabled={isUploading || disable}
        />
        {selected && (
          <Text mt={1} className={classes.browseLink}>
            {fileName && name === "bulkUtility"
              ? `${fileName} file has been selected`
              : "File has been selected"}
          </Text>
        )}
      </Paper>
      {fileError && (
        <Text className={classes.errorText} mt={1}>
          {fileError}
        </Text>
      )}
    </Box>
  );
};;

export default CustomFileUpload;
